import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Button, Input, Spinner, Chip } from "@nextui-org/react";
import { checkAIServiceConfiguration, testAIServiceConnection } from "../../../utils/helper";
import { StandardPrimaryButton } from "../../../components/StandardButtons";

const AiSettings = () => {
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [configStatus, setConfigStatus] = useState(null);

  useEffect(() => {
    // Check configuration status on component mount
    const status = checkAIServiceConfiguration();
    setConfigStatus(status);
  }, []);

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    
    try {
      const result = await testAIServiceConnection();
      setTestResult(result);
    } catch (error) {
      setTestResult({
        isAvailable: false,
        error: error.message,
        responseTime: null
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">AI Service Configuration</h2>
        <p className="text-neutral-600">
          Configure and test your AI calculation service settings
        </p>
      </div>

      {/* Configuration Status */}
      <Card className="border border-neutral-200 shadow-sm">
        <CardHeader className="px-6 py-4 border-b border-neutral-100 bg-gradient-to-r from-neutral-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">Service Configuration</h3>
              <p className="text-sm text-neutral-600 mt-1">Current AI service configuration status</p>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-6">
          {configStatus && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  configStatus.isConfigured ? 'bg-emerald-500' : 'bg-red-500'
                }`}></div>
                <span className="font-medium">
                  {configStatus.isConfigured ? 'Configuration Valid' : 'Configuration Issues Found'}
                </span>
              </div>
              
              {configStatus.isConfigured ? (
                <div className="text-sm text-neutral-600">
                  <div>Service URL: <code className="bg-neutral-100 px-2 py-1 rounded">{configStatus.serviceUrl}</code></div>
                </div>
              ) : (
                <div className="text-sm text-red-600">
                  <div className="font-medium mb-2">Issues:</div>
                  <ul className="list-disc list-inside space-y-1">
                    {configStatus.issues.map((issue, index) => (
                      <li key={index}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Connection Test */}
      <Card className="border border-neutral-200 shadow-sm">
        <CardHeader className="px-6 py-4 border-b border-neutral-100 bg-gradient-to-r from-neutral-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">Connection Test</h3>
              <p className="text-sm text-neutral-600 mt-1">Test the connection to your AI service</p>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-6">
          <div className="space-y-4">
            <StandardPrimaryButton
              onClick={handleTestConnection}
              isDisabled={isTesting || !configStatus?.isConfigured}
              startContent={isTesting ? <Spinner size="sm" /> : null}
              className="px-6"
            >
              {isTesting ? 'Testing Connection...' : 'Test Connection'}
            </StandardPrimaryButton>

            {testResult && (
              <Card className="border border-neutral-200 shadow-sm">
                <CardHeader className="px-6 py-4 border-b border-neutral-100 bg-gradient-to-r from-neutral-50 to-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-neutral-900">Connection Test Result</h3>
                      <p className="text-sm text-neutral-600 mt-1">Status of your AI service connection</p>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Chip
                        color={testResult.isAvailable ? "success" : "danger"}
                        variant="bordered"
                        startContent={testResult.isAvailable ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x-circle"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>
                        )}
                      >
                        {testResult.isAvailable ? 'Connection Successful' : 'Connection Failed'}
                      </Chip>
                    </div>
                    <div className="text-sm mt-1">
                      {testResult.isAvailable ? (
                        <div>
                          <div>Response time: {testResult.responseTime}ms</div>
                          <div>Service is responding correctly</div>
                        </div>
                      ) : (
                        <div>
                          <div>Error: {testResult.error}</div>
                          <div className="mt-2">
                            Please check your service configuration and network connection
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Environment Variables */}
      <Card className="border border-neutral-200 shadow-sm">
        <CardHeader className="px-6 py-4 border-b border-neutral-100 bg-gradient-to-r from-neutral-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">Required Environment Variables</h3>
              <p className="text-sm text-neutral-600 mt-1">Set these variables in your .env file</p>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                REACT_APP_HAYA_IP_ADDRESS
              </label>
              <Input
                value={process.env.REACT_APP_HAYA_IP_ADDRESS || 'Not set'}
                isReadOnly
                variant="bordered"
                className="font-mono text-sm"
                description="The IP address or URL of your HAYA AI server"
              />
            </div>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="text-sm text-emerald-800">
                <div className="font-medium mb-2">Example .env file:</div>
                <code className="block bg-white p-3 rounded border text-xs">
                  REACT_APP_HAYA_IP_ADDRESS=http://localhost:8000
                </code>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Troubleshooting */}
      <Card className="border border-neutral-200 shadow-sm">
        <CardHeader className="px-6 py-4 border-b border-neutral-100 bg-gradient-to-r from-neutral-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">Troubleshooting</h3>
              <p className="text-sm text-neutral-600 mt-1">Common issues and solutions</p>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-6">
          <div className="space-y-4 text-sm text-neutral-700">
            <div>
              <div className="font-medium mb-2">1. Service Not Configured</div>
              <div className="text-neutral-600">
                • Check that REACT_APP_HAYA_IP_ADDRESS is set in your .env file
                • Restart your development server after changing environment variables
              </div>
            </div>
            
            <div>
              <div className="font-medium mb-2">2. Connection Failed</div>
              <div className="text-neutral-600">
                • Verify the AI service is running and accessible
                • Check network connectivity and firewall settings
                • Ensure the service endpoint is correct
              </div>
            </div>
            
            <div>
              <div className="font-medium mb-2">3. Calculation Errors</div>
              <div className="text-neutral-600">
                • Check browser console for detailed error messages
                • Verify form data is complete and valid
                • Ensure calculation formulas are properly configured
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default AiSettings;
